import { useUploadMultiFiles } from "@/components/common/file-upload-context/use-upload-multi-files";
import { getImageMetadata } from "@/components/common/point-cloud-file-upload-context/image-data-upload-utils";
import {
  CommonUpdateProjectMultiFilesParams,
  UploadElementType,
} from "@/components/common/point-cloud-file-upload-context/use-upload-element-types";
import { updateProject } from "@/components/common/project-provider/update-project";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { selectCurrentUser } from "@/store/user-selectors";
import {
  parseImageFromFile,
  resizeImage,
  selectChildrenDepthFirst,
  selectIElement,
  selectRootId,
} from "@faro-lotv/app-component-toolbox";
import { assert, generateGUID, GUID } from "@faro-lotv/foundation";
import {
  IElementImg2d,
  IElementType,
  IElementTypeHint,
  isIElementUnassigned2dPhotosGroup,
} from "@faro-lotv/ielement-types";
import {
  createMutationAddUnplaced2dPhotos,
  ProjectApi,
  UploadedFileData,
} from "@faro-lotv/project-api-client";
import { useCallback } from "react";
import { pickImageFiles } from "./pick-file";

/** Parameters for updating the project with unplaced 2D photos data. */
export type UpdateProjectWithUnplaced2dPhotosDataParams = {
  /** The specific upload element type for unplaced 2D photos */
  type: UploadElementType.unplaced2dPhotos;
  /** Data extracted from the 2D photo files */
  extractedPhotosData: ImageExtractedData[];
};

/**
 * Updates the project by creating unplaced 2D photo elements.
 *
 * @param params - The parameters required to update the project with unplaced 2D photos.
 */
export async function updateProjectWithUnplaced2dPhotosData({
  appStore,
  dispatch,
  projectApi,
  files,
  uploadedFilesData,
  additionalUrls,
  extractedPhotosData,
}: CommonUpdateProjectMultiFilesParams &
  UpdateProjectWithUnplaced2dPhotosDataParams): Promise<void> {
  assert(
    files.length === uploadedFilesData.length,
    "Number of files and uploaded files data must be the same",
  );
  assert(
    files.length === extractedPhotosData.length,
    "Number of files and extracted photos data must be the same",
  );
  assert(
    files.length === additionalUrls.length,
    "Number of files and additional urls (thumbnails) must be the same",
  );

  const rootId = selectRootId(appStore.getState());
  assert(rootId, "Root ID is not defined");
  const userId = selectCurrentUser(appStore.getState())?.id;
  assert(userId, "User ID is not specified");

  const unplaced2dPhotoData: Unplaced2dPhotoData[] = files.map(
    (file, index) => ({
      file,
      uploadedFileData: uploadedFilesData[index],
      thumbnailUrl: additionalUrls[index],
      extractedData: extractedPhotosData[index],
    }),
  );

  await createUnplaced2dPhotos(projectApi, rootId, unplaced2dPhotoData, userId);

  const rootElement = selectIElement(rootId)(appStore.getState());
  assert(rootElement, "Root element should be defined");
  const unplaced2dPhotosGroup = selectChildrenDepthFirst(
    rootElement,
    isIElementUnassigned2dPhotosGroup,
  )(appStore.getState());
  // If it's the first time unplaced uploads are added, the parent group will not be created yet. That's why we check for <= 1.
  assert(
    unplaced2dPhotosGroup.length <= 1,
    "There should be only one unplaced uploads group under the project root",
  );
  // Refresh the whole project if the unplaced uploads group was not existing otherwise refresh only the unplaced uploads group
  const ancestorIds =
    unplaced2dPhotosGroup.length === 0
      ? [rootId]
      : [unplaced2dPhotosGroup[0].id];
  // Refresh the unplaced uploads group to get the newly added unplaced 2D photos
  await dispatch(updateProject({ projectApi, iElementQuery: { ancestorIds } }));
}

/** Data extracted from an image file*/
type ImageExtractedData = {
  /** The width of the image in pixels */
  width: number;
  /** The height of the image in pixels */
  height: number;
  /** The date and time when the image was captured */
  captureDate: Date;
};

/** Data needed to create an unplaced 2D photo element */
type Unplaced2dPhotoData = {
  /** The original file selected by the user */
  file: File;
  /** The uploaded file data returned from the server */
  uploadedFileData: UploadedFileData;
  /** The thumbnail URL of the uploaded image */
  thumbnailUrl: string;
  /** The data extracted from the image file */
  extractedData: ImageExtractedData;
};

/**
 * Creates unplaced 2D photo elements in the project.
 *
 * @param projectApi - The ProjectApi client used to interact with the project.
 * @param rootId - The root ID under which the unplaced 2D photos will be created.
 * @param unplaced2dPhotoData - An array of Unplaced2dPhotoData containing information about each photo to be created.
 * @param userId - The ID of the user creating the unplaced 2D photos.
 */
async function createUnplaced2dPhotos(
  projectApi: ProjectApi,
  rootId: GUID,
  unplaced2dPhotoData: Unplaced2dPhotoData[],
  userId: GUID,
): Promise<void> {
  const unplaced2dImgElements: IElementImg2d[] = unplaced2dPhotoData.map(
    ({ file, uploadedFileData, extractedData, thumbnailUrl }) => ({
      id: generateGUID(),
      name: file.name,
      type: IElementType.img2d,
      typeHint: IElementTypeHint.unplacedUpload,
      fileSize: file.size,
      fileName: file.name,
      pixelWidth: extractedData.width,
      pixelHeight: extractedData.height,
      createdAt: extractedData.captureDate.toISOString(),
      createdBy: userId,
      modifiedAt: extractedData.captureDate.toISOString(),
      modifiedBy: userId,
      rootId,
      root_Id: rootId,
      uri: uploadedFileData.downloadUrl,
      thumbnailUri: thumbnailUrl,
      md5Hash: uploadedFileData.md5,
    }),
  );
  const unplaced2dPhotosMutation = createMutationAddUnplaced2dPhotos(
    unplaced2dImgElements,
  );

  await projectApi.applyMutations([unplaced2dPhotosMutation]);
}

/**
 * Function that imports 2D photos into a project.
 *
 * @param photoFiles Optional array of files representing the 2D photos to be imported.
 *        If not provided, the user will be prompted to select files.
 */
type ImportFn = (photoFiles?: File[]) => void;

/**
 * Custom React hook that provides functionality to import 2D photos into a project.
 *
 * @returns A function that prompts the user to select 2D photos and imports them into the project.
 */
export function useImport2DPhotos(): ImportFn {
  const uploadUnplaced2dPhotos = useUploadMultiFiles();
  const { handleErrorWithToast } = useErrorHandlers();

  return useCallback(
    async (photoFiles?: File[]): Promise<void> => {
      try {
        if (!photoFiles) {
          photoFiles = await pickImageFiles();
        }
        if (photoFiles.length) {
          const files = Array.from(photoFiles);
          const allThumbnailFiles = await Promise.all(
            files.map(async (file) => await resizeImage(file)),
          );
          const extractedPhotosData = await Promise.all(
            files.map(async (file) => {
              const { captureDate } = await getImageMetadata(file);
              const { width, height } = await parseImageFromFile(file);
              return {
                width,
                height,
                captureDate,
              };
            }),
          );
          await uploadUnplaced2dPhotos({
            files,
            additionalFiles: allThumbnailFiles,
            type: UploadElementType.unplaced2dPhotos,
            extractedPhotosData,
          });
        }
      } catch {
        handleErrorWithToast({
          title: "Import 2D photos",
          error: "Error while importing 2D photos.",
        });
      }
    },
    [uploadUnplaced2dPhotos, handleErrorWithToast],
  );
}
